// lib/hooks/useCooldown.ts
import { useEffect, useState, useCallback, useMemo } from 'react';

const PREFIX = ' creditFlow:cooldown:';

const now = () => Date.now();
const toNum = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);


const getRemaining = (storageKey: string) => {
  try {
    const expiry = toNum(localStorage.getItem(storageKey));
    if (!expiry) {
      localStorage.removeItem(storageKey);
      return 0;
    }
    const leftMs = expiry - now();
    if (leftMs <= 0) {
      localStorage.removeItem(storageKey);
      return 0;
    }
    return Math.ceil(leftMs / 1000);
  } catch {
    return 0;
  }
};


const sweepExpired = () => {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) {
        const left = getRemaining(k);
        if (left === 0) toRemove.push(k);
      }
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch {}
};

export type Cooldown = {
  isActive: boolean;
  secondsLeft: number;
  start: () => void;
  clear: () => void;
};

const useCooldown = (rawKey: string | null | undefined, seconds: number): Cooldown => {
  const storageKey = useMemo(() => (rawKey ? `${PREFIX}${rawKey}` : null), [rawKey]);

  const [secondsLeft, setSecondsLeft] = useState(0);

  const tick = useCallback(() => {
    if (!storageKey) {
      setSecondsLeft(0);
      return 0;
    }
    const left = getRemaining(storageKey);
    setSecondsLeft(left);
    return left;
  }, [storageKey]);

  useEffect(() => {
    sweepExpired();

    if (!storageKey) {
      setSecondsLeft(0);
      return;
    }

    tick();

    const id = window.setInterval(tick, 1000);

    const onStorage = (e: StorageEvent) => {
      if (e.key === storageKey) tick();
    };
    window.addEventListener('storage', onStorage);

    return () => {
      clearInterval(id);
      window.removeEventListener('storage', onStorage);
    };
  }, [storageKey, tick]);

  const start = useCallback(() => {
    if (!storageKey) return;
    try {
      const expiry = now() + seconds * 1000;
      localStorage.setItem(storageKey, String(expiry));
      setSecondsLeft(seconds);
    } catch {}
  }, [storageKey, seconds]);

  const clear = useCallback(() => {
    if (!storageKey) return;
    try {
      localStorage.removeItem(storageKey);
      setSecondsLeft(0);
    } catch {}
  }, [storageKey]);

  return {
    isActive: secondsLeft > 0,
    secondsLeft,
    start,
    clear
  };
};

export default useCooldown;
